// src/api/heygenLiveAvatar.js
// Helper for creating a LiveAvatar session token using HeyGen's API.
// NOTE: For security, the HeyGen API key should be provided via environment
//       variable REACT_APP_HEYGEN_API_KEY and NOT hard-coded in source.

const LIVEAVATAR_SESSION_TOKEN_URL =
  "https://api.liveavatar.com/v1/sessions/token";

/**
 * Request a LiveAvatar session token from HeyGen.
 *
 * This implementation calls HeyGen directly from the browser using the API key
 * in REACT_APP_HEYGEN_API_KEY. In production, it's strongly recommended to
 * proxy this through your own backend so the key is never exposed to clients.
 *
 * @returns {Promise<string>} session token string
 */
export async function createLiveAvatarSessionToken() {
  const apiKey = "4decf212-1301-11f1-a99e-066a7fa2e369";

  if (!apiKey) {
    throw new Error(
      "Missing REACT_APP_HEYGEN_API_KEY. Please add your HeyGen API key to the .env file."
    );
  }

  const avatarId = process.env.REACT_APP_HEYGEN_AVATAR_ID || "513fd1b7-7ef9-466d-9af2-344e51eeb833";
  const voiceId = process.env.REACT_APP_HEYGEN_VOICE_ID || "b2bd6569-a537-4342-aeca-a1f15d2a2c97";
  const contextId = process.env.REACT_APP_HEYGEN_CONTEXT_ID||  "24c8096d-644e-41a8-a6b3-3ad7d8b00f0c";
  const language = process.env.REACT_APP_HEYGEN_LANGUAGE || "en";

  if (!avatarId) {
    throw new Error(
      "Missing REACT_APP_HEYGEN_AVATAR_ID. Please add your LiveAvatar avatar_id from the HeyGen dashboard to the .env file."
    );
  }

  if (!voiceId || !contextId) {
    throw new Error(
      "Missing REACT_APP_HEYGEN_VOICE_ID or REACT_APP_HEYGEN_CONTEXT_ID. Please add your voice_id and context_id from the HeyGen dashboard to the .env file."
    );
  }

  const response = await fetch(LIVEAVATAR_SESSION_TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
    },
    // FULL mode requires avatar_id and avatar_persona.
    body: JSON.stringify({
      mode: "FULL",
      avatar_id: avatarId,
      avatar_persona: {
        voice_id: voiceId,
        context_id: contextId,
        language,
      },
    }),
  });

  if (!response.ok) {
    let errorText = "";
    try {
      const errorData = await response.json();
      errorText =
        errorData.error ||
        errorData.message ||
        JSON.stringify(errorData, null, 2);
    } catch {
      errorText = await response.text().catch(() => "");
    }

    throw new Error(
      `Failed to create LiveAvatar session token (${response.status}): ${errorText}`
    );
  }

  const data = await response.json();

  // The API commonly returns the token nested under `data`, e.g.:
  // { code: 1000, data: { session_token: "..." }, message: "Session token created successfully" }
  const payload = data && data.data ? data.data : data;

  // Be tolerant of different field names from the API
  const token =
    payload.session_token ||
    payload.sessionToken ||
    payload.token ||
    payload.sessionTokenId ||
    null;

  if (!token) {
    throw new Error(
      "LiveAvatar token response did not include a recognizable token field."
    );
  }

  return token;
}

export default {
  createLiveAvatarSessionToken,
};

